import { useQuery } from "@tanstack/react-query";
import { useOutletContext, useSearchParams } from "react-router";
import { api } from "../../api/client";
import { useMenus } from "../../api/queries";
import { Alert, SelectField } from "../../components/ui";
import type { ShellContext } from "../AppShell";
import { QrCard } from "./QrCard";
import { QrPrint } from "./QrPrint";

interface Loc {
  id: string;
  name: string;
  primary: boolean;
  publicUrl: string;
}

/**
 * Coduri QR (prototype page `qr`): the code and its downloads, then the print preview (A6 tent, 5 × 5 cm sticker).
 * With more locations (owner 2026-11-04) each has its own code, chosen here (`?loc=` from Setări → Locații).
 */
export function QrPage() {
  const { membership } = useOutletContext<ShellContext>();
  const r = membership.restaurant;
  const menus = useMenus(r.id);
  const live = (menus.data ?? []).some((m) => m.publicVisible && !m.archived);
  const [params, setParams] = useSearchParams();
  const locs = useQuery({
    queryKey: ["locations", r.id],
    queryFn: () => api<{ locations: Loc[] }>("GET", `/restaurants/${r.id}/locations`),
  });
  const list = locs.data?.locations ?? [];
  const chosen = list.find((l) => l.id === params.get("loc")) ?? list.find((l) => l.primary);
  const url = chosen && !chosen.primary ? chosen.publicUrl : r.publicUrl;
  const name = chosen && !chosen.primary ? `${r.name} · ${chosen.name}` : r.name;
  const slug = chosen && !chosen.primary ? `${r.slug}-${chosen.publicUrl.split("/").pop()}` : r.slug;

  return (
    <>
      <div className="stack" style={{ gap: 6 }}>
        <h1 className="h1">Coduri QR</h1>
        <p className="page-sub" style={{ margin: 0, maxWidth: 680 }}>
          {list.length > 1
            ? "Fiecare locație are codul ei: arată același meniu, cu adresa, telefonul și programul locației. Codul nu trebuie retipărit când actualizezi meniul."
            : "Un singur cod pentru toate meniurile active. Duce mereu la același link, deci nu trebuie retipărit când actualizezi meniul."}
        </p>
      </div>
      {list.length > 1 && (
        <div style={{ maxWidth: 360 }}>
          <SelectField
            id="qr-loc"
            label="Locația"
            value={chosen?.id ?? ""}
            onChange={(e) => setParams({ loc: e.target.value }, { replace: true })}
            options={list.map((l) => [l.id, l.primary ? `${l.name} (principală)` : l.name] as const)}
          />
        </div>
      )}
      {menus.isSuccess && !live && (
        <Alert tone="warning">
          Niciun meniu nu este publicat încă. Codul funcționează, dar clienții vor vedea „Meniul nu este
          publicat încă” până publici.
        </Alert>
      )}
      <div style={{ maxWidth: 560 }}>
        <QrCard key={url} publicUrl={url} slug={slug} restaurantName={name} />
      </div>
      <div style={{ maxWidth: 560 }}>
        <QrPrint key={url} publicUrl={url} restaurantName={name} />
      </div>
    </>
  );
}
